import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import AdsAPIs from "../../apiRoutes/Ads";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { deliveryLabel } from "../../utils/deliveryLabel";
import { formatRelativeTime } from "../../utils/helperFunctions";
import {
  GET_AD_REQUEST_QUERY_KEY,
  GET_AD_REQUESTS_QUERY_KEY,
} from "../../utils/constants/queryKeys";
import { Button } from "../ui/button";
import QcHistory from "../common/QcHistory";
import DeliveredVideo from "./DeliveredVideo";

/**
 * Rollup: anything sent back to the agency > anything still needing a reviewer >
 * all reviewed. Shared with the modal's hero-strip chip so the two can never
 * disagree, and matching the backend's qc_summary precedence.
 *
 * changes_requested outranks pending because sending one delivery back leaves
 * its siblings holding at pending — calling that "QC pending" would put the
 * request back in the reviewer's queue when it is the agency's turn.
 */
export const qcRollup = (deliveries) =>
  deliveries.some((d) => d.qc_status === "changes_requested")
    ? "changes_requested"
    : deliveries.some((d) => d.qc_status === "pending")
      ? "pending"
      : "approved";

const isAwaiting = (d) =>
  d.qc_status === "pending" || d.qc_status === "changes_requested";

/**
 * Every note ever asked about each delivery, newest first, keyed by delivery id.
 *
 * Read from the round log rather than from AdDeliver.qc_feedback, because
 * resubmit clears qc_feedback — so relying on it made the ask disappear from
 * beside the video exactly when the re-uploaded version arrived and the
 * reviewer needed to check it against what they had asked for.
 */
const notesByDelivery = (history) => {
  const map = new Map();
  for (const round of history ?? []) {
    if (round.action !== "request_changes") continue;
    for (const hook of round.affected_hooks ?? []) {
      if (!hook.feedback) continue;
      const list = map.get(hook.id) ?? [];
      list.push({
        feedback: hook.feedback,
        created_on: round.created_on,
        performed_by_name: round.performed_by_name,
        // Resolved for THIS delivery, not for the round: a round covering two
        // videos is only half answered when one comes back.
        resolved: (round.resolved_hooks ?? []).some((h) => h.id === hook.id),
      });
      map.set(hook.id, list);
    }
  }
  // History arrives oldest-first; the newest ask is the one worth reading.
  for (const list of map.values()) list.reverse();
  return map;
};

/** Initials for the note avatar — first letters of the first two words. */
const initialsOf = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const NoteAuthor = ({ name, at, label = "Marked" }) => (
  <div className="flex flex-wrap items-center gap-x-2">
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#7f7ad5] text-[10px] font-semibold text-white">
      {initialsOf(name)}
    </span>
    <span className="text-sm font-medium text-gray-800">{name || "—"}</span>
    <span className="text-xs text-gray-500">
      · {label}
      {at ? ` · ${formatRelativeTime(at)}` : ""}
    </span>
  </div>
);

/**
 * Every note already sent about this video, newest first, in a scroller beside
 * the player rather than behind a modal — the reviewer is comparing the new cut
 * against what was asked for, which is impossible if reading the ask means
 * covering up the video.
 */
const SentNotes = ({ notes }) => {
  if (!notes.length) return null;
  return (
    // h-full so the panel fills whatever height the column gives it, letting it
    // finish level with the video next to it instead of stopping short.
    <div className="flex h-full min-h-0 flex-col rounded-lg border border-gray-200">
      <p className="shrink-0 border-b border-gray-100 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        Previous changes ({notes.length})
      </p>
      {/* The list takes the remaining height and scrolls inside it, so a video
          on its fifth round never pushes the compose box off screen. The max-h
          is the stacked-layout fallback, where there is no fixed height to fill. */}
      <div className="max-h-52 flex-1 overflow-y-auto divide-y divide-gray-100 sm:max-h-none">
        {notes.map((note, i) => (
          <div key={`${note.created_on}-${i}`} className="px-3 py-2">
            <NoteAuthor
              name={note.performed_by_name}
              at={note.created_on}
              label="Asked"
            />
            <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">
              {note.feedback}
            </p>
            <p
              className={`mt-1 text-[11px] font-medium ${
                note.resolved ? "text-emerald-700" : "text-amber-700"
              }`}
            >
              {note.resolved ? "Re-uploaded" : "Awaiting re-upload"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * One video with its own note box. The reviewer watches it, writes what is
 * wrong, and saves — per video, so closing the page loses nothing.
 *
 * The box starts empty even for a delivery already sent back: the note that was
 * sent shows above it, read-only, and a new note is a new round rather than an
 * edit of the old one.
 */
const QcVideoBlock = ({ delivery, requestId, pastNotes, disabled }) => {
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const saved = delivery.qc_draft || "";
  const [text, setText] = useState(saved);
  // Compose mode. A video with no note is always composing (there is nothing to
  // read yet); one with a note reads as a comment until it is clicked.
  const [editing, setEditing] = useState(!saved);
  // Whether entering edit mode was a deliberate click, which is the only time
  // the box should steal focus.
  const [focusOnEdit, setFocusOnEdit] = useState(false);
  const authorName = delivery.qc_draft_by_name || "";

  // Adopt the server's value only when it actually changes, so a background
  // refetch never clobbers what is being typed. Comparing against the current
  // text instead would leave the box stuck: sending the round clears qc_draft
  // server-side, and text that matches neither "" nor the new empty value would
  // survive as a phantom unsaved edit.
  const lastSaved = useRef(saved);
  useEffect(() => {
    if (lastSaved.current !== saved) {
      lastSaved.current = saved;
      setText(saved);
      // A note that just landed becomes a comment; one that was cleared (or
      // consumed by sending the round) goes back to an empty compose box. Not a
      // deliberate edit either way, so it must not pull focus.
      setEditing(!saved);
      setFocusOnEdit(false);
    }
  }, [saved]);

  const dirty = text.trim() !== saved.trim();
  // Written AND stored: what the reviewer means by "I've marked this one". An
  // empty box that merely matches an empty draft is not a marked note.
  const committed = !dirty && !!saved.trim();

  const saveMutation = useMutation({
    mutationFn: () =>
      AdsAPIs.qcSaveDraft({
        axiosPrivate,
        requestId,
        deliveryId: delivery.id,
        feedback: text.trim(),
      }),
    onSuccess: (_data, _vars) => {
      // Confirm at the moment of the click. The field state below says "saved"
      // too, but a reviewer working down three videos needs to know THIS one
      // landed without having to go looking for the change.
      toast.success(
        text.trim()
          ? `${deliveryLabel(delivery)} marked for changes`
          : `${deliveryLabel(delivery)} no longer marked`
      );
      queryClient.invalidateQueries({
        queryKey: [...GET_AD_REQUEST_QUERY_KEY, requestId],
      });
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.error_message || e?.message || "Could not save note"
      ),
  });

  // No qc_feedback fallback needed: 0009 backfills every open round into the
  // log, so the log is the single source for what was asked.
  const notes = pastNotes;

  return (
    // Video left, note right. min-w-0 on both halves so the break-all raw URL
    // inside the player card can't push the row wider than the page.
    //
    // The rail marks the WHOLE row once this video is flagged, not just the
    // field: the reviewer's question when scrolling is "which of these am I
    // sending back", and that has to be answerable without reading each box.
    // Rose, not emerald — emerald is approval everywhere else in this app, and
    // rose is the colour of the Send-back button this feeds.
    <div
      className={`flex flex-col gap-3 rounded-r border-l-[3px] pl-3 py-2 sm:flex-row ${
        committed ? "border-rose-400 bg-rose-50/40" : "border-transparent"
      }`}
    >
      <div className="min-w-0 sm:basis-3/5">
        <DeliveredVideo delivery={delivery} />
      </div>

      {/* min-h-0 so the notes panel below can shrink and scroll instead of
          stretching the row past the video's height. */}
      <div className="flex min-w-0 flex-col gap-2 sm:min-h-0 sm:basis-2/5">
        {/* Compose first, history under it: writing the note is the job, the old
            asks are reference.

            A written note reads as a note, not as a form field left lying open.
            The textarea only exists while composing; once saved it renders as a
            comment with who wrote it and when, and the row's rail says it is
            going back. */}
        {editing ? (
          <div className="rounded-lg px-3">
            {saved && (
              <NoteAuthor name={authorName} at={delivery.qc_draft_at} />
            )}
            {/* Focus only when the reviewer chose to edit, never on mount.
                Every video without a note renders one of these, so an
                unconditional autoFocus meant N boxes racing to grab focus — the
                last one won and the browser scrolled it into view, which is why
                loading or refreshing the page dumped you partway down it. */}
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What needs to change in this video?"
              rows={4}
              autoFocus={focusOnEdit}
              disabled={disabled || saveMutation.isPending}
              className="mt-2 w-full resize-y rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:bg-gray-50"
            />
            <div className="mt-2 flex items-center gap-2">
              <Button
                size="sm"
                className="bg-[#7f7ad5] hover:bg-[#6b66c4] text-white"
                disabled={disabled || saveMutation.isPending || !dirty}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isPending ? "Saving…" : "Save"}
              </Button>
              {/* Cancel abandons the edit and restores what is stored — it does
                  not empty the note. Removing a note is saving it empty, which
                  is what takes the video out of the round. */}
              <Button
                size="sm"
                variant="outline"
                disabled={disabled || saveMutation.isPending}
                onClick={() => {
                  setText(saved);
                  if (saved) setEditing(false);
                }}
              >
                Cancel
              </Button>
              {saved && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-rose-700"
                  disabled={disabled || saveMutation.isPending}
                  onClick={() => setText("")}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        ) : (
          /* An explicit Edit control, not a clickable card: nothing about a
             bordered block of text says it can be changed, so the note read as
             final. The card itself is no longer a button — one <button> wrapping
             the whole note also made the text unselectable, which matters when
             the point is to re-read what you asked for. */
          <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3">
            <NoteAuthor name={authorName} at={delivery.qc_draft_at} />
            <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">
              {saved}
            </p>
            {/* Action under the note, plain text — the same place a comment
                thread puts them. */}
            <button
              type="button"
              onClick={() => {
                setFocusOnEdit(true);
                setEditing(true);
              }}
              disabled={disabled}
              className="mt-2 text-[12px] font-semibold text-gray-600 hover:text-gray-900 hover:underline disabled:opacity-50"
            >
              Edit
            </button>
          </div>
        )}

        {/* Read from the round log, so it survives the agency's re-upload —
            qc_feedback is cleared then, which used to make the ask vanish just as
            the new version arrived.

            flex-1 + min-h-0 hands it the height left over after the compose box,
            so the column ends level with the video. min-h-0 is what lets it
            shrink below its content and scroll rather than overflowing the row. */}
        <div className="mt-2 min-h-0 sm:flex-1">
          <SentNotes notes={notes} />
        </div>
      </div>
    </div>
  );
};

/**
 * Delivery QC for a whole request: every video, each with its own note, then one
 * send-back for the round.
 *
 * The notes ARE the selection — a video nobody wrote about is not sent back, and
 * holds at pending rather than being dragged back with its siblings. Clearing a
 * note (save it empty) takes a video out of the round.
 */
const QcReview = ({ requestId, deliveries, history, busy, onAction }) => {
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const [confirmApprove, setConfirmApprove] = useState(false);

  const awaiting = useMemo(() => deliveries.filter(isAwaiting), [deliveries]);
  const sentBack = deliveries.filter(
    (d) => d.qc_status === "changes_requested"
  );
  const rollup = qcRollup(deliveries);
  const pastNotes = useMemo(() => notesByDelivery(history), [history]);

  // Saved notes are what gets sent. Unsaved edits are not — Save is the commit.
  const withNotes = awaiting.filter((d) => (d.qc_draft || "").trim());

  const discardMutation = useMutation({
    mutationFn: async () => {
      for (const d of withNotes) {
        await AdsAPIs.qcSaveDraft({
          axiosPrivate,
          requestId,
          deliveryId: d.id,
          feedback: "",
        });
      }
    },
    onSuccess: () => {
      toast.success("Notes discarded.");
      queryClient.invalidateQueries({
        queryKey: [...GET_AD_REQUEST_QUERY_KEY, requestId],
      });
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.error_message ||
          e?.message ||
          "Could not discard notes"
      ),
  });

  const blocked = withNotes.length > 0 || sentBack.length > 0;
  const disableAll = busy || discardMutation.isPending;

  return (
    <div className="space-y-5">
      {/* Round timeline first: what has already happened on this request, read
          before working down the videos. It overlaps the per-video panels on
          changes-requested notes, but it is the only place an APPROVE is
          recorded — who sent the request to moderation and when — and the only
          place showing which videos went back together in one round. */}
      {history.length > 0 && (
        <div>
          <QcHistory history={history} />
        </div>
      )}

      <div className="space-y-6">
        {awaiting.map((d) => (
          <QcVideoBlock
            key={d.id}
            delivery={d}
            requestId={requestId}
            pastNotes={pastNotes.get(d.id) ?? []}
            disabled={disableAll}
          />
        ))}
        {/* Already through QC — no note box, nothing left to ask for, but the
            asks it went through are the useful part: clearing QC is not the end
            of the story, and "what did we make them change" has to stay
            readable afterwards. Same two-column split so the video edges line
            up with the rows above. */}
        {deliveries
          .filter((d) => !isAwaiting(d))
          .map((d) => {
            const cleared = pastNotes.get(d.id) ?? [];
            return (
              <div key={d.id} className="flex flex-col gap-3 sm:flex-row">
                <div className="min-w-0 sm:basis-3/5">
                  <DeliveredVideo delivery={d} />
                </div>
                <div className="min-w-0 sm:basis-2/5">
                  {cleared.length > 0 ? (
                    <SentNotes notes={cleared} />
                  ) : (
                    /* Nothing was ever asked of this one — it went straight
                       through, so there is no history to show. */
                    <p className="text-xs text-gray-400">
                      Cleared QC with no changes requested.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {awaiting.length === 0 ? (
        <p className="text-xs text-gray-500">
          All deliveries have cleared QC and are with moderation.
        </p>
      ) : confirmApprove ? (
        <div className="space-y-2 border-t border-gray-100 pt-3">
          <p className="text-sm text-gray-700">
            Approve this request and send{" "}
            {awaiting.length === 1
              ? "the delivery"
              : `all ${awaiting.length} deliveries`}{" "}
            to <span className="font-medium">moderation</span>?
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={disableAll}
              onClick={() => onAction("approve")}
            >
              {busy ? "Approving…" : "Yes, send to moderation"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={disableAll}
              onClick={() => setConfirmApprove(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 border-t border-gray-100 pt-3">
          {/* Restates the round before it is sent. The marks are spread down a
              long scrolling page, so the count alone on a button is not enough
              to be sure what is about to go back. */}
          {withNotes.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border-l-[3px] border-rose-400 bg-rose-50/40 px-3 py-2">
              <span className="text-sm font-semibold text-rose-800">
                {withNotes.length === 1
                  ? "1 video marked for changes"
                  : `${withNotes.length} videos marked for changes`}
              </span>
              <span className="text-sm text-rose-700">
                {withNotes.map((d) => deliveryLabel(d)).join(", ")}
              </span>
              <span className="text-xs text-rose-600">
                — the rest stay put and move with the group later
              </span>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={disableAll || withNotes.length === 0}
              onClick={() =>
                onAction(
                  "request_changes",
                  withNotes.map((d) => ({
                    delivery_id: d.id,
                    feedback: (d.qc_draft || "").trim(),
                  }))
                )
              }
            >
              {busy
                ? "Sending…"
                : `Send back${
                    withNotes.length
                      ? ` ${withNotes.length} ${withNotes.length === 1 ? "video" : "videos"}`
                      : ""
                  }`}
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={disableAll || blocked}
              onClick={() => setConfirmApprove(true)}
            >
              Approve → Moderation
            </Button>
            {withNotes.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                disabled={disableAll}
                onClick={() => discardMutation.mutate()}
              >
                {discardMutation.isPending
                  ? "Discarding…"
                  : "Discard all notes"}
              </Button>
            )}
          </div>

          {sentBack.length > 0 ? (
            <p className="text-xs text-gray-500">
              {sentBack.map((d) => deliveryLabel(d)).join(", ")}{" "}
              {sentBack.length === 1 ? "is" : "are"} still waiting to be
              re-uploaded. Approve is unavailable until the agency sends{" "}
              {sentBack.length === 1 ? "it" : "them"} back, so a video nobody has
              re-checked cannot reach moderation.
            </p>
          ) : withNotes.length > 0 ? (
            <p className="text-xs text-gray-500">
              {withNotes.map((d) => deliveryLabel(d)).join(", ")} ha
              {withNotes.length === 1 ? "s" : "ve"} a saved note. Approve is
              unavailable until {withNotes.length === 1 ? "it is" : "they are"}{" "}
              sent back or discarded.
            </p>
          ) : (
            <p className="text-xs text-gray-400">
              {rollup === "changes_requested"
                ? "Waiting on the agency to re-upload."
                : "Write a note on any video that needs changes, or approve to send everything to moderation."}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default QcReview;
